import { format } from 'date-fns';
import PptxGenJS from 'pptxgenjs';
import {
  FONT_FAMILY,
  FONT_SIZE_P,
  HEADER_TEXT_STYLE,
  WHITE_COLOR,
} from '../components/widgets/widget-utils/ppt-export/constants';
import {
  injectMapChart,
  clearBarChartDisplayLabels,
} from '../components/widgets/widget-utils/ppt-export/patches';
import { CommonFiltersItem } from '../models/filter.model';
import { PPTformatNotesFromFilter } from '../components/widgets/widget-utils/ppt-export/pptUtils';
import { useUploadFileMutation } from '../store/api/files.api';
import { bufferToFile } from './utils';
import { FileUploadSource } from '../models/file.model';

import { saveAs } from 'file-saver';

interface PowerPointOptions {
  title: string;
  subtitle?: string;
  fileName?: string;
}

export interface SlideContent {
  title: string;
  content: (slide: PptxGenJS.Slide, pptx: PptxGenJS) => void;
}

export interface PPTFilter {
  reduxFilter: CommonFiltersItem;
  startDate: string;
  endDate: string;
}

export interface PPTExportOptions {
  presentation: PowerPointOptions;
  slides: SlideContent[];
  headerFontColor?: [];
  appliedFilter?: PPTFilter;
}

export const usePowerPointTemplate = () => {
  const [uploadToMyFiles] = useUploadFileMutation();

  const addHeaderFooter = (
    slide: PptxGenJS.Slide,
    title: string,
    _options: PPTExportOptions,
    headerImageData: string
  ) => {
    const now = new Date();
    const formattedDate = format(now, 'yyyy-MMMM-dd');
    if (headerImageData) {
      slide.addImage({
        data: headerImageData,
        x: 0,
        y: 0,
        w: '100%',
        h: 0.59,
      });
    }
    slide.addText(title, HEADER_TEXT_STYLE);

    slide.addText(`Generated: ${formattedDate}`, {
      x: 0,
      y: 5.3,
      w: '100%',
      h: 0.32,
      fontSize: FONT_SIZE_P,
      color: '67727E',
      align: 'right',
      fill: { color: 'F7F9FB' },
    });
  };

  const createCoverSlide = (
    pptx: PptxGenJS,
    title: string,
    subtitle: string,
    _options: PPTExportOptions
  ) => {
    const slide = pptx.addSlide();

    slide.background = { path: '/src/assets/ppt-export/pptx-cover.png' };

    slide.addText(title, {
      fontSize: 40,
      fontFace: FONT_FAMILY,
      color: WHITE_COLOR,
      bold: true,
      x: 0.5,
      y: '30%',
      w: '90%',
      h: 0.7,
      align: 'left',
      fit: 'shrink',
    });

    slide.addText(subtitle, {
      fontSize: 24,
      fontFace: 'Source Sans Pro',
      color: '8399AF',
      x: 0.5,
      y: '45%',
      align: 'left',
      h: 0.55,
      fit: 'shrink',
    });

    slide.addImage({
      path: '/src/assets/ppt-export/pptx-logo.svg',
      x: '75%',
      y: '80%',
      w: 1.76,
      h: 0.55,
    });

    return slide;
  };

  const createContentSlide = (
    pptx: PptxGenJS,
    slideContent: SlideContent,
    options: PPTExportOptions,
    headerImageData: string
  ) => {
    const slide = pptx.addSlide();
    addHeaderFooter(slide, slideContent.title, options, headerImageData);
    if (options.appliedFilter) {
      const { reduxFilter, startDate, endDate } = options.appliedFilter;
      const noteText = PPTformatNotesFromFilter(
        slideContent.title,
        startDate,
        endDate,
        reduxFilter
      );
      slide.addNotes(noteText);
    }
    slideContent.content(slide, pptx);
    return slide;
  };
  let pptx = new PptxGenJS();
  const generatePowerPoint = async (options: PPTExportOptions) => {
    const { presentation, slides } = options;
    const subtitle = presentation.subtitle || '';

    let headerImageData = '';
    try {
      const headerResponse = await fetch('/src/assets/ppt-export/pptx-header.svg');
      if (!headerResponse.ok) throw new Error(`HTTP ${headerResponse.status}`);
      const headerBlob = await headerResponse.blob();
      headerImageData = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(headerBlob);
      });
    } catch (err) {
      console.warn('Failed to load PPT header image, slides will omit header graphic:', err);
    }

    createCoverSlide(pptx, presentation.title, subtitle, options);

    // Clear any stale entries from a previous (possibly failed) export run
    clearBarChartDisplayLabels();

    slides.forEach((slideContent) => {
      createContentSlide(pptx, slideContent, options, headerImageData);
    });

    const fileName = presentation.fileName || 'Presentation.pptx';
    const buffer = await pptx.write({ outputType: 'blob' });
    const resultBuffer = await injectMapChart({ fileName, buffer });

    saveAs(resultBuffer, fileName);
    await uploadToMyFiles({
      file: bufferToFile(
        resultBuffer,
        fileName,
        'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      ),
      metadata: {
        SourceOrigin: FileUploadSource.POWERPOINT_EXPORT,
        DataType: FileUploadSource.POWERPOINT_EXPORT,
      },
    });
    pptx = new PptxGenJS(); // Reset pptx instance after generation
  };

  return { generatePowerPoint, pptx };
};
